import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Sidebar,
  SidebarGroup,
  SidebarGroupContent,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { ChevronDown, ChevronRight } from "lucide-react"
import { MSG_TABS } from './constants/msgTabs';
import { isTabActive } from './utils/tabUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Recursive component to render nested menu items for mobile
const MobileNestedMenu = ({ item, onTabChange, currentPath, level = 0, onClose }: {
  item: any;
  onTabChange?: (key: string) => void;
  currentPath: string;
  level?: number;
  onClose?: () => void;
}) => {
  const hasChildren = item.items && item.items.length > 0;
  const isActive = isTabActive(item.key, currentPath);
  const isParentActive = hasChildren && item.items.some(child => isTabActive(child.key, currentPath));
  const [isOpen, setIsOpen] = React.useState(isParentActive); // Start expanded if parent is active
  
  // Auto-expand parent items when active
  React.useEffect(() => {
    if (isParentActive) {
      setIsOpen(true);
    }
  }, [isParentActive]);

  const handleTabChange = (key: string) => {
    onTabChange?.(key);
    onClose?.(); // Close the sheet after navigation
  };

  if (!hasChildren) {
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
        >
          <button
            onClick={() => handleTabChange(item.key)}
            className={`flex items-center gap-3 w-full text-left py-3 px-4 rounded-md transition-colors duration-200 ${level > 0 ? 'pl-8' : ''} ${
              isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            {item.icon && (
              <FontAwesomeIcon
                icon={item.icon}
                className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
              />
            )}
            <span className={`text-sm ${isActive ? "text-primary font-medium" : "text-muted-foreground"}`}>
              {item.label}
            </span>
          </button>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem key={item.key}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/collapsible w-full">
        <CollapsibleTrigger 
          className={`flex w-full items-center px-4 py-3 text-left font-medium transition-colors duration-200 ${level > 0 ? 'pl-8' : ''} ${
            isActive || isParentActive 
              ? 'text-primary bg-primary/5' 
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          }`}
        >
          {item.icon && (
            <FontAwesomeIcon
              icon={item.icon}
              className={`w-4 h-4 flex-shrink-0 ${isActive || isParentActive ? 'text-primary' : 'text-muted-foreground'}`}
            />
          )}
          <span className="text-sm ml-3">{item.label}</span>
          <ChevronDown className={`ml-auto h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((child) => (
              <MobileNestedMenu 
                key={child.key} 
                item={child} 
                onTabChange={onTabChange} 
                currentPath={currentPath}
                level={level + 1}
                onClose={onClose}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
};

interface MSGMobileNavigationProps {
  active?: string;
  onTabChange?: (key: string) => void;
}

export function MSGMobileNavigation({ active, onTabChange }: MSGMobileNavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Get the current section name for display
  const getCurrentSectionName = () => {
    if (!active) return 'Menu';
    const rootTab = active.split('.')[0];
    const tab = MSG_TABS.find(t => t.key === rootTab);
    return tab ? tab.label : 'Menu';
  };

  return (
    <div className="md:hidden fixed bottom-6 right-6 z-50 bg-card rounded-full p-1 shadow-lg border border-border">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            size="lg"
            className="h-14 px-4 rounded-full shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-2 min-w-fit"
          >
            <Menu className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-medium hidden sm:inline-block">{getCurrentSectionName()}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80 p-0 bg-card">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-left">Management System Guide</SheetTitle>
          </SheetHeader>
          <div className="flex-1 h-full overflow-y-auto">
            <SidebarContent className="flex-1 h-full overflow-y-auto">
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {MSG_TABS.map((item) => (
                      <MobileNestedMenu 
                        key={item.key} 
                        item={item} 
                        onTabChange={onTabChange} 
                        currentPath={active || ''}
                        onClose={() => setIsOpen(false)}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
